import Table, { ColumnsType } from 'antd/lib/table';
import {
  Invoice,
  InvoiceApprovalType,
  InvoiceStatus,
  invoiceStatusKorean,
} from '../../modules/invoices';
import { priceToString } from '../../util/utils';
import moment from 'moment';
import {
  Alert,
  Button,
  Col,
  Popconfirm,
  Row,
  Space,
  Tooltip,
  Tag as ANTDTag,
} from 'antd';
import {
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import Title from 'antd/lib/typography/Title';
import styled from 'styled-components';
import { styledTable } from '../styled/mixin';
import { useEffect, useState } from 'react';
import { USER_ROLE_NAME, User } from '../../modules/auth';

const StyledDiv = styled.div`
  ${styledTable}
`;

interface InvoiceListComponentProps {
  data: Invoice[];
  user: User;
  isProjectLevel: boolean;
  moveToDetailPage: (invoiceId: string) => void;
  moveToCreatePage: () => void;
  onDelete: (invoiceId: string) => void;
  downloadFile: (data: any) => void;
  bulkUpdateInvoiceStatus?: (
    selectedValues: number[],
    approvalType: InvoiceApprovalType,
    declineReason?: string,
  ) => void;
  moveToProjectDetailPage?: (projectId: number) => void;
  openMappingTaxInvoiceModal?: (invoiceId: any) => void;
}

function InvoiceListComponent({
  data,
  user,
  isProjectLevel,
  moveToDetailPage,
  moveToCreatePage,
  onDelete,
  downloadFile,
  bulkUpdateInvoiceStatus,
  moveToProjectDetailPage,
  openMappingTaxInvoiceModal,
}: InvoiceListComponentProps) {
  const [selectedValues, setSelectedValues] = useState<any>([]);
  const [selectedRows, setSelectedRows] = useState<any>([]);

  useEffect(() => {
    setSelectedValues([]);
  }, []);

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      setSelectedValues([...selectedRowKeys]);
      setSelectedRows([...selectedRows]);
    },
    selectedRowKeys: selectedValues,
  };

  const [enableBulkEditInvoice, setEnableBulkEditInvoice] =
    useState<boolean>(false);

  const columns: ColumnsType<Invoice> = [
    {
      title: '거래처명',
      dataIndex: ['vendor', 'application', 'general', 'name'],
      ellipsis: true,
      defaultSortOrder: 'ascend',
      sorter: (a: Invoice, b: Invoice) =>
        a.vendor.application.general.name.localeCompare(
          b.vendor.application.general.name,
        ),
      width: '13%',
      render: (text: any, record: any) => (
        <>
          <span
            className="clickable-table-item"
            onClick={() => moveToDetailPage('' + record.id)}
          >
            {text}
          </span>
        </>
      ),
    },
    {
      title: '용도',
      dataIndex: 'category',
      ellipsis: true,
      sorter: (a: Invoice, b: Invoice) =>
        a.category === null || b.category === null
          ? -1
          : a.category.localeCompare(b.category),
      width: '7%',
    },
    {
      title: '공정',
      dataIndex: 'gongjong',
      ellipsis: true,
      render: (text: any, record: any) => <>{text === null ? '기타' : text}</>,
      sorter: (a: Invoice, b: Invoice) =>
        a.gongjong === null || b.gongjong === null
          ? -1
          : a.gongjong.localeCompare(b.gongjong),
      width: '7%',
    },
    {
      title: '청구일',
      dataIndex: 'billingDate',
      width: '10%',
      sorter: (a: Invoice, b: Invoice) =>
        a.billingDate.localeCompare(b.billingDate),
      render: (text: any, record: any, index: any) =>
        record.usedDate === null
          ? ''
          : moment(record.billingDate).format('YYYY-MM-DD'),
    },
    {
      title: '제출자',
      dataIndex: ['submittedBy', 'name'],
      width: '6%',
      render: (text: any, record: any) => <>{text === null ? '' : text}</>,
    },
    {
      title: '상태',
      dataIndex: 'status',
      width: '7%',
      render: (text: any, record: Invoice) =>
        invoiceStatusKorean[record.status],
    },
    {
      title: '외상거래',
      dataIndex: 'isCreditTransaction',
      width: '7%',
      render: (text: any, record: Invoice) => (text === true ? '예' : '아니오'),
    },
    // {
    //   title: isProjectLevel ? '설명' : '공사명',
    //   dataIndex: isProjectLevel ? 'description' : ['project', 'name'],
    //   ellipsis: true,
    //   sorter: isProjectLevel
    //     ? undefined
    //     : (a: Invoice, b: Invoice) =>
    //         a?.project?.name.localeCompare(b?.project?.name),
    //   width: '10%',
    //   render: (text: any, record: any) => (
    //     <Tooltip title={text}>{text}</Tooltip>
    //   ),
    // },
    {
      title: '공급가액(원)',
      width: '10%',
      align: 'right',
      render: (value: any, record: any, index: any) =>
        priceToString(record.price - record.vat),
    },
    {
      title: '부가세(원)',
      dataIndex: 'vat',
      width: '9%',
      align: 'right',
      render: (value: any, record: any, index: any) => priceToString(value),
    },
    {
      title: '합계금액(원)',
      dataIndex: 'price',
      sorter: (a: Invoice, b: Invoice) => a.price - b.price,
      width: '10%',
      align: 'right',
      render: (value: any, record: any, index: any) => priceToString(value),
    },
    {
      title: '지급액(원)',
      width: '10%',
      align: 'right',
      render: (value: any, record: any, index: any) =>
        priceToString(
          record.payments.reduce(
            (acc: number, cur: any) => acc + cur.amount,
            0,
          ),
        ),
    },
    {
      title: '미지급액(원)',
      width: '10%',
      align: 'right',
      render: (value: any, record: any, index: any) => {
        const totalPayment = record.payments.reduce(
          (acc: number, cur: any) => acc + cur.amount,
          0,
        );
        return priceToString(record.price - totalPayment);
      },
    },
    {
      title: '',
      key: 'action',
      dataIndex: '',
      align: 'center',
      width: '5%',
      render: (text, record, index) => (
        <Space size="small">
          {(record.status === InvoiceStatus.DECLINED ||
            record.status === InvoiceStatus.DRAFT) &&
            (record.submittedBy.id === user?.id ||
              user?.roleName === USER_ROLE_NAME.ADMIN) && (
              <div onClick={(e) => e.stopPropagation()}>
                <Popconfirm
                  title="정말 삭제하시겠습니까?"
                  data-test={`project-list-item-${index}`}
                  onConfirm={() => onDelete('' + record.id)}
                >
                  <Button size="small">삭제</Button>
                </Popconfirm>
              </div>
            )}
          {(record.status === InvoiceStatus.APPROVED ||
            record.status === InvoiceStatus.PAYMENT_COMPLETED) &&
            record.vat !== 0 &&
            user?.roleName === USER_ROLE_NAME.ADMIN && (
              <Button
                size="small"
                onClick={() => {
                  if (openMappingTaxInvoiceModal)
                    openMappingTaxInvoiceModal(record.id);
                }}
              >
                설정
              </Button>
            )}
        </Space>
      ),
    },
  ];

  if (!isProjectLevel) {
    columns.splice(7, 0, {
      title: '공사명',
      dataIndex: ['project', 'name'],
      ellipsis: true,
      sorter: (a: Invoice, b: Invoice) =>
        a?.project?.name.localeCompare(b?.project?.name),
      width: '10%',
      render: moveToProjectDetailPage
        ? (text: any, record: any) => (
            <>
              <span
                className="clickable-table-item"
                onClick={() => moveToProjectDetailPage(record.project.id)}
              >
                <Tooltip title={text}>{text}</Tooltip>
              </span>
            </>
          )
        : (text: any, record: any) => <Tooltip title={text}>{text}</Tooltip>,
    });
    columns.splice(13, 0, {
      title: '매입증빙',
      width: '6%',
      align: 'center',
      render: (value: any, record: any, index: any) => {
        let result = '';
        let color = '';
        const _totalConnectedTaxInvoicePrice = record.taxInvoices.reduce(
          (acc: number, cur: any) => acc + cur.taxAmt + cur.supplyValue,
          0,
        );
        const _totalInvoicePrice = record.price;
        if (record.vat === 0) {
          return 'N/A';
        }
        if (_totalConnectedTaxInvoicePrice === 0) {
          result = '아니오';
          color = 'red';
        } else if (_totalInvoicePrice === _totalConnectedTaxInvoicePrice) {
          result = '예';
          color = 'blue';
        } else if (_totalInvoicePrice > _totalConnectedTaxInvoicePrice) {
          result = '진행중';
          color = 'green';
        } else {
          result = '분할';
          color = 'olive';
        }
        return result === '아니오' ? (
          <ANTDTag color={color}>{result}</ANTDTag>
        ) : (
          <Tooltip
            placement="topLeft"
            title={
              <>
                <p>매입증빙내역</p>
                <ul>
                  {record.taxInvoices.map((item: any, index: any) => (
                    <li key={index}>
                      {item.reportingDate} |{' '}
                      {priceToString(item.supplyValue + item.taxAmt)}원
                    </li>
                  ))}
                </ul>
                {record.description && (
                  <>
                    <p>비고</p>
                    <p style={{ whiteSpace: 'pre-wrap' }}>
                      {record.description}
                    </p>
                  </>
                )}
              </>
            }
          >
            <ANTDTag color={color}>{result}</ANTDTag>
          </Tooltip>
        );
      },
    });
  } else {
    columns.splice(12, 0, {
      title: '증빙확인',
      width: '6%',
      align: 'center',
      render: (value: any, record: any, index: any) => {
        let result = '';
        let color = '';
        const _totalConnectedTaxInvoicePrice = record.taxInvoices.reduce(
          (acc: number, cur: any) => acc + cur.taxAmt + cur.supplyValue,
          0,
        );
        const _totalInvoicePrice = record.price;
        if (record.vat === 0) {
          return 'N/A';
        }
        if (_totalConnectedTaxInvoicePrice === 0) {
          result = '아니오';
          color = 'red';
        } else if (_totalInvoicePrice === _totalConnectedTaxInvoicePrice) {
          result = '예';
          color = 'blue';
        } else if (_totalInvoicePrice > _totalConnectedTaxInvoicePrice) {
          result = '진행중';
          color = 'green';
        } else {
          result = '분할';
          color = 'olive';
        }
        return result === '아니오' ? (
          <ANTDTag color={color}>{result}</ANTDTag>
        ) : (
          <Tooltip
            placement="topLeft"
            title={
              <>
                <p>매입증빙내역</p>
                <ul>
                  {record.taxInvoices.map((item: any, index: any) => (
                    <li key={index}>
                      {item.reportingDate} |{' '}
                      {priceToString(item.supplyValue + item.taxAmt)}원
                    </li>
                  ))}
                </ul>
                {record.description && (
                  <>
                    <p>비고</p>
                    <p style={{ whiteSpace: 'pre-wrap' }}>
                      {record.description}
                    </p>
                  </>
                )}
              </>
            }
          >
            <ANTDTag color={color}>{result}</ANTDTag>
          </Tooltip>
        );
      },
    });
  }

  return (
    <StyledDiv>
      {enableBulkEditInvoice && user && (
        <Row style={{ marginBottom: '10px' }}>
          <Col flex={1}>
            <Alert
              message={`청구서 왼쪽의 체크박스를 선택 후 상태를 변경하세요.`}
              type="info"
              closable={false}
              action={
                <Space>
                  <Button
                    size="small"
                    type="ghost"
                    disabled={
                      selectedRows.length === 0 ||
                      selectedRows.filter(
                        (row: any) => row.status !== InvoiceStatus.APPROVING,
                      ).length !== 0 ||
                      selectedRows.filter(
                        (row: any) => row.approver.id !== user.id,
                      ).length !== 0
                    }
                    onClick={() => {
                      bulkUpdateInvoiceStatus &&
                        bulkUpdateInvoiceStatus(
                          selectedValues,
                          InvoiceApprovalType.REQUEST_APPROVAL,
                        );
                      setEnableBulkEditInvoice(false);
                    }}
                  >
                    승인
                  </Button>
                  <Button
                    size="small"
                    type="ghost"
                    disabled={
                      selectedRows.length === 0 ||
                      selectedRows.filter(
                        (row: any) => row.status !== InvoiceStatus.APPROVED,
                      ).length !== 0
                    }
                    onClick={() => {
                      bulkUpdateInvoiceStatus &&
                        bulkUpdateInvoiceStatus(
                          selectedValues,
                          InvoiceApprovalType.PAYMENT_APPROVAL,
                        );
                      setEnableBulkEditInvoice(false);
                    }}
                  >
                    지급완료
                  </Button>
                  <Button
                    size="small"
                    type="ghost"
                    onClick={() => {
                      setEnableBulkEditInvoice(false);
                      setSelectedRows([]);
                      setSelectedValues([]);
                    }}
                  >
                    창 닫기
                  </Button>
                </Space>
              }
            />
          </Col>
        </Row>
      )}
      <Row></Row>
      <Row justify="space-between">
        <Col>
          <Title level={isProjectLevel ? 3 : 4}>청구서</Title>
        </Col>
        <Col>
          <Space>
            {isProjectLevel && (
              <Button
                size="small"
                onClick={() => moveToCreatePage()}
                icon={<PlusOutlined rev={undefined} />}
              >
                추가
              </Button>
            )}
            {!isProjectLevel && bulkUpdateInvoiceStatus && (
              <Button
                size="small"
                onClick={() => setEnableBulkEditInvoice(true)}
                icon={<EditOutlined rev={undefined} />}
              >
                상태변경
              </Button>
            )}
            <Button
              size="small"
              icon={<DownloadOutlined rev={undefined} />}
              onClick={() => downloadFile(data)}
            >
              다운로드
            </Button>
          </Space>
        </Col>
      </Row>

      {data && (
        <Table<Invoice>
          rowKey="id"
          columns={columns}
          dataSource={data}
          rowSelection={
            enableBulkEditInvoice
              ? { ...rowSelection, checkStrictly: false }
              : undefined
          }
          // expandable={{
          //   // showExpandColumn: false,
          //   defaultExpandAllRows: true,
          // }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '50', '100', '300'],
            position: ['bottomCenter'],
          }}
          locale={{
            triggerDesc: '내림차순으로 정렬하려면 클릭하세요.',
            triggerAsc: '오름차순으로 정렬하려면 클릭하세요.',
            cancelSort: '정렬을 취소하려면 클릭하세요.',
          }}
          summary={(pageData) => {
            let totalPrice = 0;
            let totalVat = 0;
            let totalAmount = 0;

            pageData.forEach(({ price, vat, payments }) => {
              totalPrice += price;
              totalVat += vat;
              totalAmount += payments.reduce(
                (acc: number, cur: any) => acc + cur.amount,
                0,
              );
            });
            return (
              <>
                {isProjectLevel ? (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}></Table.Summary.Cell>
                    <Table.Summary.Cell index={1}></Table.Summary.Cell>
                    <Table.Summary.Cell index={2}></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}></Table.Summary.Cell>
                    <Table.Summary.Cell index={4}></Table.Summary.Cell>
                    <Table.Summary.Cell index={5}></Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">
                      Total:
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={7} align="right">
                      {priceToString(totalPrice - totalVat)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={8} align="right">
                      {priceToString(totalVat)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={9} align="right">
                      {priceToString(totalPrice)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={10} align="right">
                      {priceToString(totalAmount)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={11} align="right">
                      {priceToString(totalPrice - totalAmount)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={12}></Table.Summary.Cell>
                  </Table.Summary.Row>
                ) : (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}></Table.Summary.Cell>
                    <Table.Summary.Cell index={1}></Table.Summary.Cell>
                    <Table.Summary.Cell index={2}></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}></Table.Summary.Cell>
                    <Table.Summary.Cell index={4}></Table.Summary.Cell>
                    <Table.Summary.Cell index={5}></Table.Summary.Cell>
                    <Table.Summary.Cell index={6}></Table.Summary.Cell>
                    <Table.Summary.Cell index={7} align="right">
                      Total:
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={8} align="right">
                      {priceToString(totalPrice - totalVat)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={9} align="right">
                      {priceToString(totalVat)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={10} align="right">
                      {priceToString(totalPrice)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={10} align="right">
                      {priceToString(totalAmount)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={11} align="right">
                      {priceToString(totalPrice - totalAmount)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={12}></Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              </>
            );
          }}
        />
      )}
    </StyledDiv>
  );
}
export default InvoiceListComponent;
